import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DebouncedText from "components/DebouncedText";
import {
  CustomCaseProperty,
  CustomPropertyCategory,
  CustomPropertyType,
  CustomPropertyVisibility,
  DeletePropSelectOptionsDocument,
  MigrateCustomPropertyTypeDocument,
  UpsertCasePropertyMutationVariables,
} from "generated/graphql";
import { distinct } from "helpers/coll";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCaseProperties, upsertCaseProperty } from "redux/slices/lensEditor";
import SelectOptions from "components/SelectOptions/SelectOptions";
import { Edit } from "@mui/icons-material";
import PropertySelectOptionDialog from "routes/lens-editor/profile-builder/PropertySelectOptionDialog";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";

interface Props {
  open: boolean;
  close: () => void;
  customProp?: CustomCaseProperty;
  lensId: number;
}
const CasePropertyDialog: FC<Props> = ({ open, close, customProp, lensId }) => {
  const [property, setProperty] = useState<CustomCaseProperty | undefined>(customProp);
  const [key, setKey] = useState<string>(property?.key || "");
  const caseProperties = useAppSelector(selectCaseProperties);
  const [displayName, setDisplayName] = useState<string>(property?.displayName || "");
  const [description, setDescription] = useState(property?.description || "");
  const [isHidden, setIsHidden] = useState<boolean>(property?.isHidden ?? false);
  const [dataType, setDataType] = useState<CustomPropertyType | "">(property?.dataType ?? "");
  const [collectVisibility, setCollectVisibility] = useState<CustomPropertyVisibility>(
    property?.collectVisibility || CustomPropertyVisibility.Hidden
  );
  const [selectOptions, setSelectOptions] = useState<string[]>(property?.selectOptions || []);
  const [showMigrateSelectOptionDialog, setMigrateSelectOptionDialog] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const casePropertyExists = caseProperties.find((cp) => cp.key === key);
  const canSave =
    key.length > 0 && displayName.length > 0 && (property ? true : casePropertyExists === undefined) && dataType !== "";
  const deletedSelectOptions = (property?.selectOptions || []).filter((o) => !selectOptions.includes(o));

  const save = async () => {
    if (dataType !== undefined) {
      const currentProperty: UpsertCasePropertyMutationVariables = {
        key,
        displayName,
        description,
        lensId,
        isHidden,
        dataType: dataType !== "" ? dataType : CustomPropertyType.Text,
        collectVisibility,
        selectOptions,
      };
      const migratePropOrDeleteSelectOptionFunc = async () => {
        const oldDataType = property?.dataType;
        if (oldDataType !== undefined && oldDataType !== dataType && dataType !== "") {
          return runGqlMutation(MigrateCustomPropertyTypeDocument, {
            lensId,
            key,
            dataType,
            propCategory: CustomPropertyCategory.CaseCustomProperty,
          });
        } else if (deletedSelectOptions.length > 0) {
          return runGqlMutation(DeletePropSelectOptionsDocument, {
            lensId,
            key,
            selectOptionsToDelete: deletedSelectOptions,
            propCategory: CustomPropertyCategory.CaseCustomProperty,
          });
        } else {
          return Promise.resolve();
        }
      };

      await migratePropOrDeleteSelectOptionFunc()
        .then(() =>
          snackpatch(upsertCaseProperty(currentProperty), {
            successMessage: "Case Property Success",
            errorMessage: "Case Property Error",
          })
        )
        .catch(() =>
          enqueueSnackbar(`Failed to update case property`, {
            variant: "error",
          })
        );

      close();
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{property ? "Update" : "New"} Property</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2,
          }}>
          <FormControl fullWidth required>
            <DebouncedText
              value={key}
              textFieldProps={{
                disabled: !!property,
                helperText: "No spaces",
              }}
              onChange={(key) => {
                setKey(key);
              }}
              formatter={(text) => text.toLowerCase().replace(/\s/g, "")}
              delayMs={1}
              label="Key"
            />
          </FormControl>
          {!property && casePropertyExists && (
            <Alert sx={{ mb: 2 }} severity="warning">
              <b>{casePropertyExists.displayName}</b> already exists with this key. Please change the key.
            </Alert>
          )}
          <FormControl fullWidth required>
            <TextField
              value={displayName}
              onChange={(evt) => {
                setDisplayName(evt.target.value);
              }}
              label="Display Name"
            />
          </FormControl>
          <FormControl fullWidth required>
            <TextField
              value={description}
              onChange={(evt) => {
                setDescription(evt.target.value);
              }}
              label="Description"
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Data Type</InputLabel>
            <Select
              disabled={!!property && property.dataType === CustomPropertyType.Text}
              label="Data Type"
              value={dataType}
              onChange={(e) => {
                const dt = e.target.value as CustomPropertyType;
                setDataType(dt);
                if (dt !== CustomPropertyType.Select) {
                  setSelectOptions([]);
                }
                if (dt === CustomPropertyType.Json) {
                  setIsHidden(true);
                }
              }}>
              <MenuItem value={CustomPropertyType.Text}>Text</MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Date
                }
                value={CustomPropertyType.Date}>
                Date
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Number
                }
                value={CustomPropertyType.Number}>
                Number
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Select
                }
                value={CustomPropertyType.Select}>
                Select
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Json
                }
                value={CustomPropertyType.Json}>
                JSON
              </MenuItem>
            </Select>
          </FormControl>
          {dataType === CustomPropertyType.Select && (
            <div className="flex mx-4">
              {showMigrateSelectOptionDialog && (
                <PropertySelectOptionDialog
                  onClose={() => setMigrateSelectOptionDialog(false)}
                  availableSelectOptions={selectOptions}
                  onSuccess={(oldValue: string, newValue: string) => {
                    setMigrateSelectOptionDialog(false);
                    const newSelectOptions = selectOptions.map((o) => (o === oldValue ? newValue : o));
                    setSelectOptions(newSelectOptions);
                    if (property !== undefined) {
                      setProperty({ ...property, selectOptions: newSelectOptions });
                    }
                  }}
                  propertyType={CustomPropertyCategory.CaseCustomProperty}
                  propertyKey={key}
                />
              )}
              {property !== undefined && (
                <div>
                  <Tooltip title="Rename Option" sx={{ mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMigrateSelectOptionDialog(true);
                      }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <SelectOptions
                options={selectOptions}
                setOptions={(options) => {
                  const distinctValues = distinct(options);
                  setSelectOptions(distinctValues);
                }}
              />
            </div>
          )}
          <FormControl fullWidth>
            <InputLabel>Collect Visibility</InputLabel>
            <Select
              size="small"
              label="Collect Visibility"
              value={collectVisibility}
              onChange={(e) => {
                const cv = e.target.value as CustomPropertyVisibility;
                setCollectVisibility(cv);
              }}>
              <MenuItem value={CustomPropertyVisibility.Hidden}>Hidden</MenuItem>
              <MenuItem value={CustomPropertyVisibility.Visible}>Visible</MenuItem>
              <MenuItem value={CustomPropertyVisibility.Editable}>Editable</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={dataType === CustomPropertyType.Json}>
            <FormLabel>Hidden in Pattern</FormLabel>
            <RadioGroup row value={isHidden} onChange={(e) => setIsHidden(e.target.value === "true" ? true : false)}>
              <FormControlLabel value={false} control={<Radio />} label="No" />
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={close}>
          Cancel
        </Button>
        <Button disabled={!canSave} color="primary" sx={{ ml: 2 }} onClick={save} variant="contained">
          {!!property && property.dataType !== dataType
            ? "Migrate and "
            : deletedSelectOptions.length > 0
              ? "Delete select options and "
              : ""}
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CasePropertyDialog;
